import React, { FC, ReactElement, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import throttle from 'lodash/throttle';
import cns from 'classnames';
import { Link as ScrollLink } from 'react-scroll';
import { navSections, internalizationState, getAllStyle } from '../../../stores/reducers/sections';
import Icon from '../../Icon';
import './Header.sass';
import './HeaderNav.sass';
import routes from '../../../config/routes';
import { 
  getContentDataHeader,
  getIsLoggedIn,
  setLoggedIn,
  getExhibitionNavmenuButtons,
  getSponsorPages,
  getIsRegistrationRequired,
  getNavmenuButtonsVisibility,
  getEventKey
} from '../../../stores/reducers/userData';
import LoginModal from '../../Modals/LoginModal/LoginModal';
import { userLogout } from '../../../api';
import AlertModal from '../../Modals/AlertModal';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';
import localeFieldResolver from '../../../helpers/locale-field-finder';

interface HeaderProps {
  className: string;
  isAlwaysSticky?: boolean;
}

const Header: FC<HeaderProps> = ({ className, isAlwaysSticky }): ReactElement => {
  const sections = useSelector(navSections);
  const { activeLangState, internalization, isAwailableLocaleSwitcher } = useSelector(internalizationState);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [alertModalData, setAlertModalData] = useState<{ isOpen: boolean; close: () => void; text: string }>({
    isOpen: false,
    close: () => setAlertModalData({ ...alertModalData, isOpen: false }),
    text: '',
  });
  const isLoggedIn = useSelector(getIsLoggedIn);
  const sponsorPages = useSelector(getSponsorPages);
  const { exhibitionButton1, exhibitionButton2, exhibitionButtonLive } = useSelector(getExhibitionNavmenuButtons);
  const isRegistrationRequired = useSelector(getIsRegistrationRequired);
  const btnsVisible = useSelector(getNavmenuButtonsVisibility);
  const showLogin = !isLoggedIn && isRegistrationRequired;
  const showLogout = isLoggedIn;
  const location = useLocation();
  const { backendURL, logo } = useSelector(getContentDataHeader);
  const dispatch = useDispatch();
  const history = useHistory();
  const styles = useSelector(getAllStyle);
  const eventKey = useSelector(getEventKey);

  const localizeData = {
    registration_ru: 'Регистрация',
    registration_en: 'Registration',
    login_ru: 'Войти',
    login_en: 'Login',
    logout_ru: 'Выйти',
    logout_en: 'Logout',
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);

  const scrollHandler = throttle(() => {
    const { scrollY } = window;
    setIsSticky(scrollY > 0);
  }, 100);

  const refMenu = useRef<HTMLUListElement>(null);
  const [linkSize, setLinkSize] = useState<number>(styles.textSize.menu);

  const handleMenuResize = () => {
    if (styles.textSize.locMenuSize || linkSize > parseInt(styles.textSize.menu)) {
      setLinkSize(parseInt(styles.textSize.menu));
      return;
    }

    if (refMenu.current && refMenu.current.children.length && window.innerWidth > 1024) {
      const menuWrapperWidth = refMenu.current.offsetWidth;
      let linksTotalWidth = 0;
      Array.from(refMenu.current.children).forEach((elem) => {
        const link = elem as HTMLLIElement;
        if (!link.classList.contains('hidden-desktop')) {
          linksTotalWidth += link.offsetWidth;
        }
      });      

      if (menuWrapperWidth < linksTotalWidth) {
        setLinkSize(linkSize - 1);
      } else if (menuWrapperWidth > linksTotalWidth + (refMenu.current.children[refMenu.current.children.length - 1] as HTMLLIElement).offsetWidth + 100) {
        if (linkSize < parseInt(styles.textSize.menu)) {
          setLinkSize(linkSize + 1);
        }
      }
    } else {
      setLinkSize(parseInt(styles.textSize.menu));
    }
  }

  // useEffect(() => {
  //   handleMenuResize();
  // }, [linkSize]);

  useEffect(() => {
    handleMenuResize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, linkSize]);

  useEffect(() => {
    window.addEventListener('resize', handleMenuResize);
    return () => {
      window.removeEventListener('resize', handleMenuResize, false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkSize]);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler, false);
    if (location.hash === '#login') {
      setIsModalOpen(true);
      location.hash = '';
    }

    return () => {
      window.removeEventListener('scroll', scrollHandler, false);
    };
  }, [location.hash, scrollHandler]);

  const handleLogout = () => {
    setMenuOpen(false);
    userLogout(eventKey).then((response) => {
      if ([200, 204].includes(response.status)) {
        if (history.location.pathname === routes.LIVE) history.push(routes.HOME);
        dispatch(setLoggedIn(false));
      } else {
        setAlertModalData({ ...alertModalData, isOpen: true, text: 'Возникла ошибка, повторите попытку позже' });
      }
    });
  };

  const scrollWithOffset = (el: HTMLElement) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -164;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const openLogin = () => {
    window.dispatchEvent((window as any).clickEnterHeader);
    setIsModalOpen(true);
  };

  const openLoginMobile = () => {
    setMenuOpen(false);
    openLogin();
  };

  const getRoute = (pageType: string = '', pageId: string): string => {
    // const pageTypes = ['custom.theme.list', 'custom.theme', 'double.header.list', 'partner', 'partners.list', 'sp']
    switch (pageType) {
      case 'custom.theme.list':
        return routes.DEMOZONE
      case 'custom.theme':
        return `${routes.DEMOZONE_INFO}?code=${pageId}`
      case 'double.header.list':
        return routes.EXHIBITION
      case 'partners.list':
        return routes.EXHIBITION_OF_SPONSORS
      case 'partner':
        const partner = sponsorPages.find((item: any): any => item.id === pageId)
        return `${routes.SPONSOR_PAGE}?name=${partner.title.toLowerCase()}`
      case 'sp':
        return `${routes.STATIC}/${pageId}`
      default:
        return '/'
    }
  };

  const firstExhibitionButton = () => {
    if (exhibitionButton1 && btnsVisible[0]) {
      return (
        <li className={cns(
          'header-min-nav--item additional__link header-first-button opacity gradient'
        )}>
          <Link to={getRoute(exhibitionButton1.pageType, exhibitionButton1.page)} className="exhibition-item" style={{fontSize: linkSize}}>
            { localeFieldResolver(exhibitionButton1, 'name', activeLangState) }
          </Link>
        </li>
      )
    }

    return (<></>);
  };

  const secondExhibitionButton = () => {
    if (exhibitionButton2 && btnsVisible[1]) {
      return (
        <li className={cns(
          'header-min-nav--item additional__link header-second-button opacity gradient'
        )}>
          <Link to={getRoute(exhibitionButton2.pageType, exhibitionButton2.page)} className="exhibition-item" style={{fontSize: linkSize}}>
            { localeFieldResolver(exhibitionButton2, 'name', activeLangState) }
          </Link>
        </li>
      )
    }

    return (<></>);
  };

  const liveExhibitionButton = () => {
    if (exhibitionButtonLive && btnsVisible[2]) {
      return (
        <li className={cns(
          'header-min-nav--item additional__link header-third-button opacity gradient'
        )}>
          <Link to={routes.LIVE} className="watch-item" style={{fontSize: linkSize}} onClick={() => window.dispatchEvent((window as any).clickLiveHeader)}>
            { localeFieldResolver(exhibitionButtonLive, 'name', activeLangState) }
          </Link>
        </li>
      )
    }

    return (<></>);
  };

  const headerScrollOpacityClass = (): string => {
    let result = '';
    if (isSticky && location.pathname === routes.HOME) {
      result = 'bg-header--sticky';
    } else if (!isSticky && location.pathname === routes.HOME) {
      result = 'bg-header--ontop';
    } else {
      result = 'bg-header';
    }
    return result;
  };


  const linkClassName = (arr: any[]): string => {
    return '';
    // return arr.length < 7 ? 'normal-link' : '';
  };

  return (
    styles.header.isHeaderVisible &&
    <>
      <LoginModal goal="clickEnterOnModPage" isOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} />
      <AlertModal isOpen={alertModalData.isOpen} close={alertModalData.close} text={alertModalData.text} />
      <div
        className={cns(
          'header',
          location.pathname === routes.HOME ? 'header-optional' : 'header-overlay',
          // (isSticky || isAlwaysSticky) && 'sticky-menu bg-header',
          headerScrollOpacityClass(),
          className,
          menuOpen && 'open-menu',
          location.pathname === '/demozone' && 'header-demozone'
        )}>
        <div className="container-fluid">
          <div className="header__wrapper">
            {location.pathname === routes.HOME ? (
              <ScrollLink className="header__logo" to="layout" spy smooth duration={500}>
                {logo && <img src={`${backendURL}${logo}`} alt="Home" />}
              </ScrollLink>
            ) : (
              <HashLink className="header__logo" to={`${routes.HOME}#layout`}>
                {logo && <img src={`${backendURL}${logo}`} alt="Home" />}
              </HashLink>
            )}
            <div className={cns('mobile-navi', sections.length < 7 ? 'normal-menu' : '' )} role="button" tabIndex={0} onClick={closeMenu}>
              <div className="mobile-navi__wrap" role="button" tabIndex={0} onClick={(e) => e.stopPropagation()}>
                {location.pathname === routes.HOME ? (
                  <ScrollLink className="mobile-logo" to="layout" spy smooth duration={500} onClick={closeMenu}>
                    <button className="modal-close" type="button" onClick={() => setMenuOpen(false)}>
                      <Icon name="close" />
                    </button>
                    {logo && <img src={`${backendURL}${logo}`} alt="Home" />}
                  </ScrollLink>
                ) : (
                  <HashLink className="mobile-logo" to={`${routes.HOME}#layout`} onClick={closeMenu}>
                    <button className="modal-close" type="button" onClick={() => setMenuOpen(false)}>
                      <Icon name="close" />
                    </button>
                    {logo && <img src={`${backendURL}${logo}`} alt="Home" />}
                  </HashLink>
                )}
                <ul className="header__menu" ref={refMenu}>
                  {
                    (internalization && isAwailableLocaleSwitcher) &&
                      <li>
                        <LanguageSwitcher type="mobile" />
                      </li>
                  }
                  {sections.map((section: any, index: number, arr: any[]) => section.isNavigational && (
                    <li className={linkClassName(arr)} key={section.id} style={{fontSize: linkSize}}>
                      {location.pathname === routes.HOME ? (
                        <ScrollLink
                          to={section.id.toString()}
                          spy
                          smooth
                          offset={-164}
                          duration={500}
                          onClick={closeMenu}>
                          { localeFieldResolver(section, 'name', activeLangState) }
                        </ScrollLink>
                      ) : (
                        <HashLink to={`${routes.HOME}#${section.id}`} scroll={scrollWithOffset} onClick={closeMenu}>
                          { localeFieldResolver(section, 'name', activeLangState) }
                        </HashLink>
                      )}
                    </li>
                  ))}
                  {showLogin && (
                    <>
                    <li className="hidden-desktop additional_button">
                      <Link to={routes.REGISTER} onClick={closeMenu} style={{fontSize: linkSize}}>
                        { localizedName('registration_ru') }
                      </Link>
                    </li>
                    <li className="hidden-desktop additional_button">
                      <button type="button" onClick={openLoginMobile} style={{fontSize: linkSize}}>
                        { localizedName('login_ru') }
                      </button>
                    </li>
                    </>
                  )}
                  {showLogout && (
                    <li className="hidden-desktop additional_button">
                      <button type="button" onClick={() => handleLogout()} style={{fontSize: linkSize}}>
                        { localizedName('logout_ru') }
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <ul className="header-min-nav">
              {
                (internalization && isAwailableLocaleSwitcher) && <LanguageSwitcher type="desktop" />
              }
              {
                firstExhibitionButton()
              }
              {
                secondExhibitionButton()
              }
              {
                liveExhibitionButton()
              }
              {showLogin && (
                <>
                <li className="additional_button">
                  <Link to={routes.REGISTER} style={{fontSize: linkSize}}>
                    { localizedName('registration_ru') }
                  </Link>
                </li>
                <li className="additional_button">
                  <button type="button" style={{fontSize: linkSize}} onClick={openLogin}>
                    { localizedName('login_ru') }
                  </button>
                </li>
                </>
              )}
              {showLogout && (
                <li className="additional_button">
                  <button type="button" style={{fontSize: linkSize}} onClick={() => handleLogout()}>
                    { localizedName('logout_ru') }
                  </button>
                </li>
              )}
            </ul>
            <button className="header__hamburger" type="button" onClick={() => setMenuOpen(!menuOpen)}>
              <Icon name="hamburger" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
